"use client";

import Link from "next/link";
import { FaSquareFacebook, FaLinkedin } from "react-icons/fa6";
import { Phone, Mail, MapPinned } from "lucide-react";

import { AiFillInstagram } from "react-icons/ai";
import { AFSLogo } from "../assets/logos";
import { COMPANY, SOCIALS, UI } from "@/lib/constants";
import { usePathname } from "next/navigation";
// import CurrencyButton from "../navigation/currency-button";
import { version, formatVersion } from "@/config/version";
// import { ThemeToggle } from "@/components/theme/theme-toggle";

import Container from "../ui/container";

export default function Footer({
  isAuthenticated = false,
}: {
  isAuthenticated?: boolean;
}) {
  const pathname = usePathname();
  const isDashboardPage = pathname?.startsWith("/dashboard");

  if (isDashboardPage) return null;

  return (
    <footer className='bg-background text-foreground border-t border-muted pt-10 pb-16'>
      <Container className='px-4'>
        <div className='grid grid-cols-1 md:grid-cols-5 gap-8'>
          <div className='mb-8 md:mb-0 md:col-span-3 lg:col-span-2'>
            <Link href='/'>
              <AFSLogo width={80} height={33} />
            </Link>
            <p className='mt-4 text-muted-foreground'>{COMPANY.description}</p>
            <p className='mt-2 text-sm text-muted-foreground'>
              ABN: {COMPANY.abn.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4")}
            </p>
            <p className='mt-2 text-sm text-muted-foreground'>
              Trading Hours:
              <br />
              {COMPANY.workHours}
              <br />
              {COMPANY.publicHolidays}
            </p>
          </div>
          <div>
            <h3 className='text-lg font-semibold mb-4'>Discover</h3>
            <ul className='space-y-2'>
              <li>
                <Link href='/about' className='hover:text-primary'>
                  About
                </Link>
              </li>
              <li>
                <Link
                  href='/rock-board/products'
                  className='text-primary'
                >
                  Rock Board<sup>®</sup>
                </Link>
              </li>
              <li>
                <Link href='/xcelr8/products' className='text-[#f6eb0f]'>
                  XCELR8<sup>®</sup>
                </Link>
              </li>
              <li>
                <Link href='/distributors' className='hover:text-primary'>
                  Find a Distributor
                </Link>
              </li>
              <li>
                <Link href='/contact' className='hover:text-primary'>
                  Contact
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className='text-lg font-semibold mb-4'>Support</h3>
            <ul className='space-y-2'>
              {isAuthenticated && (
                <li>
                  <Link href='/my-fleethub' className='hover:text-primary'>
                    My FleetHub
                  </Link>
                </li>
              )}
              <li>
                <Link
                  href='/shipping-deliveries'
                  className='hover:text-primary'
                >
                  Shipping & Deliveries
                </Link>
              </li>
              <li>
                <Link href='/return-policy' className='hover:text-primary'>
                  Return Policy
                </Link>
              </li>
              {isAuthenticated && (
                <li>
                  <Link
                    href='/applications/distributor'
                    className='hover:text-primary'
                  >
                    Become a Distributor
                  </Link>
                </li>
              )}
              <li>
                <Link
                  href='/applications/trade-account'
                  className='hover:text-primary'
                >
                  Apply for Trade Account
                </Link>
              </li>
            </ul>
          </div>
          <div className='flex flex-col gap-2'>
            <h3 className='text-lg font-semibold mb-4'>Contact Us</h3>
            <Link
              href={UI.googleMapsLink}
              target='_blank'
              className='flex items-center gap-2'
            >
              <MapPinned className='h-8 w-8 mr-2 text-foreground' />
              {COMPANY.address.street}, {COMPANY.address.city}
            </Link>
            <Link
              href={`tel:${COMPANY.phone}`}
              className='flex items-center gap-2'
            >
              <Phone className='h-5 w-5 mr-2 text-foreground' />
              {COMPANY.phone.replace(/(\+61)(\d)(\d{4})(\d{4})/, '$1 $2 $3 $4')}
            </Link>
            <Link
              href={`mailto:${COMPANY.email}`}
              className='flex items-center gap-2'
            >
              <Mail className='h-5 w-5 mr-2 text-foreground' />
              {COMPANY.email}
            </Link>
          </div>
          <div className='md:col-span-2'>
            <h3 className='text-lg font-semibold mb-4'>Connect With Us</h3>
            <ul className='flex space-x-4 mb-6'>
              <li>
                <Link
                  href={SOCIALS.facebook}
                  target='_blank'
                  className='text-muted-foreground hover:text-primary'
                >
                  <FaSquareFacebook className='h-6 w-6' />
                  <span className='sr-only'>Facebook</span>
                </Link>
              </li>
              {/* <li>
                <Link
                  href={SOCIALS.twitter}
                  target='_blank'
                  className='text-muted-foreground hover:text-primary'
                >
                  <FaTwitter className='h-6 w-6' />
                  <span className='sr-only'>Twitter</span>
                </Link>
              </li> */}
              <li>
                <Link
                  href={SOCIALS.instagram}
                  target='_blank'
                  className='text-muted-foreground hover:text-primary'
                >
                  <AiFillInstagram className='h-6 w-6' />
                  <span className='sr-only'>Instagram</span>
                </Link>
              </li>
              <li>
                <Link
                  href={SOCIALS.linkedin}
                  target='_blank'
                  className='text-muted-foreground hover:text-primary'
                >
                  <FaLinkedin className='h-6 w-6' />
                  <span className='sr-only'>LinkedIn</span>
                </Link>
              </li>
            </ul>
            <div className='flex flex-col space-y-3'>
              <Link
                href={COMPANY.googleReview}
                target='_blank'
                className='inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary'
              >
                <svg
                  viewBox='0 0 24 24'
                  className='w-5 h-5'
                  fill='currentColor'
                >
                  <path d='M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z' />
                </svg>
                Leave us a review
              </Link>
            </div>
          </div>
          {/* <div className='flex items-end text-sm text-muted-foreground md:col-span-1'>
            <div className='flex items-center gap-2'>
              <span>Theme:</span>
              <ThemeToggle variant='outline' />
            </div>
          </div> */}
        </div>
        <div className='mt-8 pt-8 border-t border-muted'>
          <div className='flex flex-wrap justify-center gap-4 mb-4 text-sm text-muted-foreground'>
            <Link href='/terms-conditions' className='hover:text-primary'>
              Terms & Conditions
            </Link>
            <Link href='/privacy-policy' className='hover:text-primary'>
              Privacy Policy
            </Link>
            <Link href='/cookie-policy' className='hover:text-primary'>
              Cookie Policy
            </Link>
            <Link href='/sitemap.xml' className='hover:text-primary'>
              Sitemap
            </Link>
          </div>
          <div className='flex flex-col justify-between items-center gap-2'>
            <p className='text-muted-foreground text-center'>
              &copy; {new Date().getFullYear()} {COMPANY.name}. All rights
              reserved.
            </p>
            <p className='text-sm text-muted-foreground text-center'>
              Designed by{" "}
              <span className='text-primary'>
                IONA Global Pty Ltd
              </span>
            </p>
            <p className='text-xs text-muted-foreground'>
              {formatVersion(version)}
            </p>
          </div>
        </div>
      </Container>
    </footer>
  );
}
